// ============================================================================
// @torre/expenses — Estados financieros (estado de resultados, balance, flujo)
// ============================================================================
// Los tres estados salen de los mismos movimientos (gastos, ingresos y nómina)
// y cuadran entre sí por construcción:
//
//   · ESTADO DE RESULTADOS (devengado): ingresos − costo del servicio (nómina,
//     pagada o estimada del padrón, + infraestructura: nube, hosting y APIs que
//     consume el producto) − gastos de operación (incluye las plataformas, las
//     herramientas del equipo) − depreciación ± resultado financiero −
//     impuestos = utilidad neta.
//   · FLUJO DE EFECTIVO (caja): solo lo que de verdad se movió, separado en
//     operación, inversión (equipo, rendimientos) y financiamiento (gastos de
//     la empresa que alguien cubrió con su dinero y hay que reembolsarle).
//   · BALANCE GENERAL a una fecha: efectivo acumulado + equipo neto = por
//     reembolsar + nómina devengada sin pago + resultados acumulados.
//
// Diferencias entre devengado y caja, que son las que explican el balance:
//   · la nómina estimada del padrón (`source: padron`) es gasto pero no salió
//     de caja: queda como pasivo;
//   · el equipo (`hardware`) sale de caja al comprarse, pero al resultado entra
//     por su depreciación en línea recta (`DEPRECIATION_MONTHS`).
//
// Lo no atribuible a la empresa (`personal`) no entra; lo que no tiene tipo de
// cambio no se inventa: se cuenta en `withoutRate`.
// ============================================================================

import { EXPENSE_CATEGORIES, lastDayOf, monthRange, periodOf, round2, type Expense, type ExpenseCategory, type Income, type ValidationResult } from "./index";

/** Vida útil del equipo, en meses (línea recta, desde el mes siguiente a la compra). */
export const DEPRECIATION_MONTHS = 36;

/** Categorías que no son gasto de operación: tienen su propio renglón. */
const COST_OF_SERVICE: ExpenseCategory = "nomina";
/** Infraestructura: también es costo del servicio (lo que cuesta operar el producto), con su renglón. */
const INFRASTRUCTURE: ExpenseCategory = "infraestructura";
const EQUIPMENT: ExpenseCategory = "hardware";
const FINANCIAL_COST: ExpenseCategory = "comisiones";
const TAXES: ExpenseCategory = "impuestos";
/** Categorías que son gasto de operación (función: `index` aún no termina de cargar al importar este módulo). */
export const opexCategories = (): ExpenseCategory[] =>
  EXPENSE_CATEGORIES.filter((c) => c !== COST_OF_SERVICE && c !== INFRASTRUCTURE && c !== EQUIPMENT && c !== FINANCIAL_COST && c !== TAXES);

export interface FinancialMonth {
  /** `YYYY-MM`; al agrupar, `2026-Q3` o `2026`. */
  period: string;

  // --- Estado de resultados (devengado) ---
  /** Cobros a clientes. */
  revenue: number;
  /** Depósitos de personas físicas y otros. */
  otherIncome: number;
  totalIncome: number;
  /** Costo del servicio: nómina + infraestructura. */
  costOfService: number;
  /** Nómina: pagada + estimada del padrón. */
  costOfServicePayroll: number;
  /** Infraestructura: nube, hosting, dominios y APIs que consume el producto. */
  costOfServiceInfrastructure: number;
  grossProfit: number;
  /** Gastos de operación por categoría (sin nómina, infraestructura, equipo, comisiones ni impuestos). */
  opex: Partial<Record<ExpenseCategory, number>>;
  /** Devoluciones recibidas: restan del gasto de operación. */
  refunds: number;
  /** Gastos de operación ya con las devoluciones restadas. */
  opexTotal: number;
  depreciation: number;
  operatingProfit: number;
  /** Rendimientos de las cuentas de la empresa. */
  financialIncome: number;
  /** Comisiones bancarias. */
  financialCost: number;
  profitBeforeTaxes: number;
  taxes: number;
  netIncome: number;

  // --- Flujo de efectivo (caja) ---
  /** Cobros (clientes + otros). */
  cashCollections: number;
  /** Nómina que sí salió de caja (la estimada del padrón no). */
  cashPayroll: number;
  /** Pagos de operación, comisiones incluidas, ya con las devoluciones restadas. */
  cashOperatingPaid: number;
  cashTaxes: number;
  cashOperating: number;
  /** Compra de equipo. */
  cashEquipment: number;
  /** Rendimientos cobrados. */
  cashInterest: number;
  cashInvesting: number;
  /** Gastos de la empresa cubiertos por una persona (reembolsables). */
  cashPartnerFunding: number;
  cashFinancing: number;
  cashNet: number;

  /** Nómina devengada sin pago registrado (estimada del padrón). */
  accruedPayroll: number;
}

const NUMERIC_KEYS = [
  "revenue",
  "otherIncome",
  "totalIncome",
  "costOfService",
  "costOfServicePayroll",
  "costOfServiceInfrastructure",
  "grossProfit",
  "refunds",
  "opexTotal",
  "depreciation",
  "operatingProfit",
  "financialIncome",
  "financialCost",
  "profitBeforeTaxes",
  "taxes",
  "netIncome",
  "cashCollections",
  "cashPayroll",
  "cashOperatingPaid",
  "cashTaxes",
  "cashOperating",
  "cashEquipment",
  "cashInterest",
  "cashInvesting",
  "cashPartnerFunding",
  "cashFinancing",
  "cashNet",
  "accruedPayroll",
] as const satisfies readonly (keyof FinancialMonth)[];

function emptyMonth(period: string): FinancialMonth {
  const m = { period, opex: {} } as FinancialMonth;
  for (const k of NUMERIC_KEYS) m[k] = 0;
  return m;
}

/** Subtotales de un periodo a partir de sus renglones (todo redondeado a centavos). */
function finish(m: FinancialMonth): FinancialMonth {
  for (const c of Object.keys(m.opex) as ExpenseCategory[]) m.opex[c] = round2(m.opex[c] || 0);
  for (const k of NUMERIC_KEYS) m[k] = round2(m[k]);
  const opexGross = (Object.values(m.opex) as number[]).reduce((s, v) => s + v, 0);
  m.totalIncome = round2(m.revenue + m.otherIncome);
  m.grossProfit = round2(m.totalIncome - m.costOfService);
  m.opexTotal = round2(opexGross - m.refunds);
  m.operatingProfit = round2(m.grossProfit - m.opexTotal - m.depreciation);
  m.profitBeforeTaxes = round2(m.operatingProfit + m.financialIncome - m.financialCost);
  m.netIncome = round2(m.profitBeforeTaxes - m.taxes);
  m.cashOperating = round2(m.cashCollections - m.cashPayroll - m.cashOperatingPaid - m.cashTaxes);
  m.cashInvesting = round2(m.cashInterest - m.cashEquipment);
  m.cashFinancing = round2(m.cashPartnerFunding);
  m.cashNet = round2(m.cashOperating + m.cashInvesting + m.cashFinancing);
  return m;
}

type StatementExpense = Pick<Expense, "date" | "amountBase" | "category"> & Partial<Pick<Expense, "period" | "personal" | "reimbursable" | "source">>;
type StatementIncome = Pick<Income, "date" | "amountBase" | "payerType"> & Partial<Pick<Income, "period" | "personal">>;

export interface FinancialMonthsResult {
  months: FinancialMonth[];
  /** Movimientos sin tipo de cambio dentro del rango: no suman. */
  withoutRate: number;
}

/**
 * Renglones de los tres estados, mes por mes, de `from` a `to` (`YYYY-MM`).
 * Para el balance hay que pedirlo desde el primer movimiento de la empresa.
 */
export function financialMonths(input: { expenses: readonly StatementExpense[]; incomes: readonly StatementIncome[]; from: string; to: string }): FinancialMonthsResult {
  const periods = input.from <= input.to ? monthRange(input.from, input.to) : [];
  const months = periods.map(emptyMonth);
  const byPeriod = new Map(months.map((m) => [m.period, m]));
  const index = new Map(periods.map((p, i) => [p, i]));
  let withoutRate = 0;

  for (const i of input.incomes) {
    if (i.personal === true) continue;
    const m = byPeriod.get(i.period || periodOf(i.date));
    if (!m) continue;
    if (i.amountBase == null) {
      withoutRate += 1;
      continue;
    }
    const v = i.amountBase;
    if (i.payerType === "devolucion") {
      m.refunds += v;
      m.cashOperatingPaid -= v;
    } else if (i.payerType === "rendimiento") {
      m.financialIncome += v;
      m.cashInterest += v;
    } else {
      if (i.payerType === "cliente") m.revenue += v;
      else m.otherIncome += v;
      m.cashCollections += v;
    }
  }

  for (const e of input.expenses) {
    if (e.personal === true) continue;
    const period = e.period || periodOf(e.date);
    const m = byPeriod.get(period);
    if (!m) continue;
    if (e.amountBase == null) {
      withoutRate += 1;
      continue;
    }
    const v = e.amountBase;
    // La nómina estimada del padrón es gasto, pero nadie registró su pago.
    const accrued = e.source === "padron";
    if (e.category === COST_OF_SERVICE) {
      m.costOfService += v;
      m.costOfServicePayroll += v;
      if (accrued) m.accruedPayroll += v;
      else m.cashPayroll += v;
    } else if (e.category === INFRASTRUCTURE) {
      // Costo del servicio en resultados; en caja es un pago de operación más.
      m.costOfService += v;
      m.costOfServiceInfrastructure += v;
      m.cashOperatingPaid += v;
    } else if (e.category === EQUIPMENT) {
      m.cashEquipment += v;
      const start = (index.get(period) as number) + 1;
      // Cada mes lleva la diferencia del acumulado redondeado: así la suma no deriva
      // por centavos y a los `DEPRECIATION_MONTHS` meses el equipo queda exactamente en cero.
      for (let k = 0; k < DEPRECIATION_MONTHS && start + k < months.length; k += 1) {
        months[start + k].depreciation += round2((v * (k + 1)) / DEPRECIATION_MONTHS) - round2((v * k) / DEPRECIATION_MONTHS);
      }
    } else if (e.category === TAXES) {
      m.taxes += v;
      m.cashTaxes += v;
    } else if (e.category === FINANCIAL_COST) {
      m.financialCost += v;
      m.cashOperatingPaid += v;
    } else {
      m.opex[e.category] = (m.opex[e.category] || 0) + v;
      m.cashOperatingPaid += v;
    }
    if (e.reimbursable === true && !accrued) m.cashPartnerFunding += v;
  }

  return { months: months.map(finish), withoutRate };
}

export type FinancialGranularity = "month" | "quarter" | "year";

/** Etiqueta del periodo al que pertenece un mes: `2026-09`, `2026-Q3` o `2026`. */
export function financialPeriodKey(period: string, granularity: FinancialGranularity): string {
  if (granularity === "month") return period;
  const [y, mo] = period.split("-");
  return granularity === "year" ? y : `${y}-Q${Math.ceil(Number(mo) / 3)}`;
}

/** Suma los meses por trimestre o por año (o los deja como están). */
export function aggregateFinancialMonths(months: readonly FinancialMonth[], granularity: FinancialGranularity): FinancialMonth[] {
  const out = new Map<string, FinancialMonth>();
  for (const m of months) {
    const key = financialPeriodKey(m.period, granularity);
    const acc = out.get(key) || emptyMonth(key);
    for (const k of NUMERIC_KEYS) acc[k] += m[k];
    for (const [c, v] of Object.entries(m.opex) as [ExpenseCategory, number][]) acc.opex[c] = (acc.opex[c] || 0) + v;
    out.set(key, acc);
  }
  return [...out.values()].map(finish);
}

/** Un solo periodo con el total de todos los meses. */
export function totalFinancialMonths(months: readonly FinancialMonth[]): FinancialMonth {
  const acc = emptyMonth("total");
  for (const m of months) {
    for (const k of NUMERIC_KEYS) acc[k] += m[k];
    for (const [c, v] of Object.entries(m.opex) as [ExpenseCategory, number][]) acc.opex[c] = (acc.opex[c] || 0) + v;
  }
  return finish(acc);
}

// --- Balance general -----------------------------------------------------------

export const BALANCE_SECTIONS = ["activo", "pasivo", "capital"] as const;
export type BalanceSection = (typeof BALANCE_SECTIONS)[number];
export const MAX_BALANCE_ITEMS = 100;

/**
 * Partida capturada a mano: lo que los movimientos no saben (saldo inicial en
 * bancos, cuentas por cobrar, un préstamo, aportaciones de los socios…).
 */
export interface BalanceItem {
  id: string;
  section: BalanceSection;
  label: string;
  /** En moneda base. Puede ser negativo (una estimación, un retiro de socios). */
  amount: number;
  /** Desde cuándo cuenta (`YYYY-MM-DD`); `null` = siempre. */
  date?: string | null;
}

export function validateBalanceItems(items: unknown): ValidationResult {
  const errors: string[] = [];
  if (!Array.isArray(items)) return { valid: false, errors: ["items: se espera una lista"] };
  if (items.length > MAX_BALANCE_ITEMS) errors.push(`items: máximo ${MAX_BALANCE_ITEMS} partidas`);
  items.forEach((raw, i) => {
    const it = (raw || {}) as Partial<BalanceItem>;
    if (!BALANCE_SECTIONS.includes(it.section as BalanceSection)) errors.push(`items[${i}].section: activo, pasivo o capital`);
    if (!String(it.label ?? "").trim()) errors.push(`items[${i}].label: requerido`);
    if (typeof it.amount !== "number" || !Number.isFinite(it.amount)) errors.push(`items[${i}].amount: debe ser un número`);
    if (it.date != null && it.date !== "" && !/^\d{4}-\d{2}-\d{2}$/.test(String(it.date))) errors.push(`items[${i}].date: debe ser YYYY-MM-DD`);
  });
  return { valid: errors.length === 0, errors };
}

export function normalizeBalanceItems(items: readonly Partial<BalanceItem>[]): BalanceItem[] {
  return items.map((it, i) => ({
    id: String(it.id || "").trim() || `partida_${i + 1}`,
    section: it.section as BalanceSection,
    label: String(it.label).trim().slice(0, 120),
    amount: round2(Number(it.amount)),
    date: it.date ? String(it.date) : null,
  }));
}

export interface BalanceSheet {
  /** Fecha de corte (`YYYY-MM-DD`). */
  asOf: string;
  assets: {
    /** Efectivo según libros: lo cobrado menos lo pagado desde el primer movimiento. */
    cash: number;
    equipmentCost: number;
    accumulatedDepreciation: number;
    equipmentNet: number;
    manual: BalanceItem[];
    total: number;
  };
  liabilities: {
    /** Gastos de la empresa que pagó una persona: se le deben. */
    reimbursable: number;
    /** Nómina devengada sin pago registrado. */
    accruedPayroll: number;
    manual: BalanceItem[];
    total: number;
  };
  equity: {
    /** Resultado de los ejercicios anteriores al año de corte. */
    retainedEarnings: number;
    /** Resultado del año de corte, hasta la fecha de corte. */
    currentEarnings: number;
    manual: BalanceItem[];
    /** Lo que falta para que las partidas a mano cuadren entre sí (0 si cuadran). */
    unreconciled: number;
    total: number;
  };
  /** Activo − (pasivo + capital): siempre 0, se deja como comprobación. */
  difference: number;
}

/**
 * Balance general a `asOf`. `months` debe venir desde el primer movimiento de
 * la empresa (ver `financialMonths`); los meses posteriores al corte se ignoran.
 */
export function balanceSheet(input: { months: readonly FinancialMonth[]; items?: readonly BalanceItem[]; asOf: string }): BalanceSheet {
  const cutoff = periodOf(input.asOf);
  const year = cutoff.slice(0, 4);
  const months = input.months.filter((m) => m.period <= cutoff);
  const sum = (k: (typeof NUMERIC_KEYS)[number], rows: readonly FinancialMonth[] = months) => round2(rows.reduce((s, m) => s + m[k], 0));
  const manual = (section: BalanceSection) => (input.items || []).filter((it) => it.section === section && (!it.date || it.date <= input.asOf));
  const total = (items: readonly BalanceItem[]) => items.reduce((s, it) => s + it.amount, 0);

  const cash = sum("cashNet");
  const equipmentCost = sum("cashEquipment");
  const accumulatedDepreciation = sum("depreciation");
  const equipmentNet = round2(equipmentCost - accumulatedDepreciation);
  const assetItems = manual("activo");
  const assets = round2(cash + equipmentNet + total(assetItems));

  const reimbursable = sum("cashPartnerFunding");
  const accruedPayroll = sum("accruedPayroll");
  const liabilityItems = manual("pasivo");
  const liabilities = round2(reimbursable + accruedPayroll + total(liabilityItems));

  const retainedEarnings = sum("netIncome", months.filter((m) => m.period.slice(0, 4) < year));
  const currentEarnings = sum("netIncome", months.filter((m) => m.period.slice(0, 4) === year));
  const equityItems = manual("capital");
  // Los movimientos cuadran solos; las partidas a mano pueden no cuadrar entre sí.
  const unreconciled = round2(assets - liabilities - retainedEarnings - currentEarnings - total(equityItems));
  const equity = round2(retainedEarnings + currentEarnings + total(equityItems) + unreconciled);

  return {
    asOf: input.asOf,
    assets: { cash, equipmentCost, accumulatedDepreciation, equipmentNet, manual: assetItems, total: assets },
    liabilities: { reimbursable, accruedPayroll, manual: liabilityItems, total: liabilities },
    equity: { retainedEarnings, currentEarnings, manual: equityItems, unreconciled, total: equity },
    difference: round2(assets - liabilities - equity),
  };
}

/** Último día del periodo de corte, sin pasar de hoy. */
export function balanceCutoff(to: string, today: string): string {
  const end = lastDayOf(to);
  return end > today ? today : end;
}
